import React, { useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '~/contexts/AppContext';
import { AgeGroup, GroupResult } from '~/types';
import { SliderAnimationState } from '../SliderPanel';

import MajorityResult from './MajorityResult';
import GenderResult from './GenderResult';
import AgeResult from './AgeResult';
import { 
    AnimationSlot,
    buildAnimationSequence,
    getCurrentAnimationSlot,
    shouldRenderSlot,
    shouldShowQnA,
    getFirstResultSlot
} from '~/util/ResultPhaseManager';

interface RatingResultUnifiedProps {
    ratingValue: number;
    ratingId: number | null;
    sliderAnimationState: SliderAnimationState;
    onAnimationComplete: () => void;
    loadingResults: { [groupString: string]: boolean };
    groupResults: { [groupString: string]: GroupResult };
    setReferenceRating: (referenceRating: number | null, referenceColor: string | null) => void;
    submitGender: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    submitBirthYear: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

export const getAgeGroup = (birthYear: number | null): AgeGroup | null => {
    if (birthYear === null) return null;

    const userAge: number = new Date().getFullYear() - birthYear;

    if (userAge > 0 && userAge < 12) return '0-11';
    if (userAge > 11 && userAge < 18) return '12-17';
    if (userAge > 17 && userAge < 25) return '18-24';
    if (userAge > 24 && userAge < 35) return '25-34';
    if (userAge > 34 && userAge < 45) return '35-44';
    if (userAge > 44 && userAge < 55) return '45-54';
    if (userAge > 54 && userAge < 65) return '55-64';
    if (userAge > 64) return '65+';

    return null;
};

const RatingResultUnified: React.FC<RatingResultUnifiedProps> = (props) => {
    const { topicData, profile } = useContext(AppContext)!;

    const [completedSlots, setCompletedSlots] = useState<AnimationSlot[]>([]);
    
    const animationSequence = useRef(buildAnimationSequence(topicData?.topic ?? null, profile)).current;
    
    const currentSlot = getCurrentAnimationSlot(completedSlots, animationSequence);
    
    const firstResultSlot = getFirstResultSlot(animationSequence);
    
    const qnaTriggeredRef = useRef(false);

    const initialGenderData = useRef(profile?.gender && profile.gender !== 'other');

    const genderResultRef = useRef<HTMLDivElement>(null);
    const hasScrolledRef = useRef(false);

    useEffect(() => {
        if (!qnaTriggeredRef.current && shouldShowQnA(currentSlot)) {
            qnaTriggeredRef.current = true;
            props.onAnimationComplete();
            
            handleSlotComplete(AnimationSlot.QnA);
        }
    }, [currentSlot, props]);

    const smoothScrollToGender = () => {
        if (!hasScrolledRef.current && !initialGenderData.current && genderResultRef.current) {
            hasScrolledRef.current = true;
            genderResultRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest'
            });
        }
    };

    const handleSlotComplete = (slot: AnimationSlot) => {
        setCompletedSlots(prev => {
            if (prev.includes(slot)) return prev;
            return [...prev, slot];
        });
    };

    const createAnimationCompleteHandler = (slot: AnimationSlot) => {
        return () => handleSlotComplete(slot);
    };

    const shouldRender = (slot: AnimationSlot): boolean => {
        return shouldRenderSlot(slot, currentSlot, completedSlots, topicData?.topic ?? null, profile);
    };

    return (
        <>            
            {shouldRender(AnimationSlot.Majority) && (
                <MajorityResult
                    ratingValue={props.ratingValue}
                    ratingId={props.ratingId}
                    onAnimationComplete={createAnimationCompleteHandler(AnimationSlot.Majority)}
                    setReferenceRating={props.setReferenceRating}
                    sliderAnimationState={props.sliderAnimationState}
                    groupResults={props.groupResults}
                    loadingResults={props.loadingResults}
                    isFirstSlide={firstResultSlot === AnimationSlot.Majority}
                />
            )}

            {shouldRender(AnimationSlot.Gender) && (
                <div ref={genderResultRef}>
                    <GenderResult
                        ratingValue={props.ratingValue}
                        ratingId={props.ratingId}
                        onAnimationComplete={createAnimationCompleteHandler(AnimationSlot.Gender)}
                        setReferenceRating={props.setReferenceRating}
                        sliderAnimationState={props.sliderAnimationState}
                        groupResults={props.groupResults}
                        submitGender={props.submitGender}
                        loadingResults={props.loadingResults}
                        isFirstSlide={firstResultSlot === AnimationSlot.Gender}
                    />
                </div>
            )}
            {(shouldRender(AnimationSlot.Age) && !props.loadingResults.gender)  && <AgeResult
                ratingValue={props.ratingValue}
                ratingId={props.ratingId}
                onAnimationComplete={createAnimationCompleteHandler(AnimationSlot.Age)}
                setReferenceRating={props.setReferenceRating}
                sliderAnimationState={props.sliderAnimationState}
                groupResults={props.groupResults}
                submitBirthYear={props.submitBirthYear}
                loadingResults={props.loadingResults}
                isFirstSlide={firstResultSlot === AnimationSlot.Age}
                shouldBeHidden={
                    !profile?.birthYear && 
                    (topicData?.topic.pvtShowGenderStats ?? false) && 
                    !initialGenderData.current
                }
                onCardVisible={smoothScrollToGender}
            />}
        </>
    );
};

export default RatingResultUnified;
